import { KanbanCargando } from "@/components/ui/kanban";
import { Skeleton, TablaSkeleton } from "@/components/ui/skeleton";

/**
 * Esqueletos del layout de un portal mientras se resuelven nav, permisos y contexto. Sin esto el
 * sidebar pintaba primero los menús fijos del front y saltaba al llegar los de la BD, y la página
 * mostraba un estado vacío que no era real.
 */

/** Items del sidebar en carga: mismo alto que un botón de la nav (py-2, 13px). */
export function EsqueletoNav({ items = 7 }: { items?: number }) {
  return (
    <div className="space-y-1.5 px-1 pt-1" aria-busy="true" aria-label="Cargando menú">
      {Array.from({ length: items }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full rounded-md" />
      ))}
    </div>
  );
}

/**
 * Página en carga con el orden del estándar de portal: descripción, fila de KPIs, pestañas, barra
 * de filtros y tabla (en móvil, tarjetas si la pantalla las usa). Cada pantalla pide solo lo que
 * pinta: `kpis={0}` sin KPIs, `tabs` con pestañas, `filtros={false}` sin barra, `tabla={false}`
 * sin tabla, `kanban` tablero en lugar de tabla, `bloques` rejilla de tarjetas (reportes, secciones
 * de dashboard) con `altoBloque`. Sin props queda la página de siempre (descripción, 4 KPIs,
 * buscador y tabla).
 */
export function EsqueletoPagina({
  kpis = 4, tabs = false, filtros = true, tabla = true, filas = 6, columnas = 5, tarjetasMovil = false,
  kanban = false, bloques = 0, altoBloque = "h-28",
}: {
  kpis?: number; tabs?: boolean; filtros?: boolean; tabla?: boolean; filas?: number; columnas?: number; tarjetasMovil?: boolean;
  kanban?: boolean; bloques?: number; altoBloque?: string;
}) {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Cargando">
      <Skeleton className="h-4 w-72 max-w-full" />
      {kpis > 0 && (
        <div className={`grid grid-cols-2 gap-3 ${kpis >= 5 ? "lg:grid-cols-5" : kpis === 3 ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}>
          {Array.from({ length: kpis }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] w-full rounded-xl" />
          ))}
        </div>
      )}
      {tabs && (
        <div className="flex gap-6 border-b pb-2.5">
          {[24, 20, 28, 20].map((w, i) => (
            <Skeleton key={i} className="h-4" style={{ width: `${w * 4}px` }} />
          ))}
        </div>
      )}
      {filtros && <Skeleton className="h-9 w-full max-w-md" />}
      {kanban && <KanbanCargando columnas={4} />}
      {bloques > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: bloques }).map((_, i) => (
            <Skeleton key={i} className={`${altoBloque} w-full rounded-lg`} />
          ))}
        </div>
      )}
      {tabla && !kanban && (
        <>
          <TablaSkeleton filas={filas} columnas={columnas} className={tarjetasMovil ? "hidden md:block" : undefined} />
          {tarjetasMovil && (
            <div className="space-y-2 md:hidden">
              {Array.from({ length: Math.min(filas, 4) }).map((_, i) => (
                <Skeleton key={i} className="h-28 w-full rounded-lg" />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Detalle en carga (una cuenta, un caso): encabezado con título y datos, pestañas si la pantalla
 * las tiene, KPIs opcionales y el cuerpo. `lateral` pinta el cuerpo como ficha (columna de 300 px a
 * la izquierda y contenido a la derecha); sin él, dos bloques a lo ancho. `encabezado={false}` para
 * cuando el encabezado ya está en pantalla y solo carga el cuerpo.
 */
export function EsqueletoDetalle({
  encabezado = true, tabs = false, kpis = 0, lateral = false,
}: {
  encabezado?: boolean; tabs?: boolean; kpis?: number; lateral?: boolean;
}) {
  return (
    <div className="space-y-5" aria-busy="true" aria-label="Cargando">
      {encabezado && (
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-7 w-72 max-w-full" />
          <Skeleton className="h-4 w-56 max-w-full" />
        </div>
      )}
      {tabs && (
        <div className="flex gap-6 border-b pb-2.5">
          {[24, 20, 28, 20].map((w, i) => (
            <Skeleton key={i} className="h-4" style={{ width: `${w * 4}px` }} />
          ))}
        </div>
      )}
      {kpis > 0 && (
        <div className={`grid grid-cols-2 gap-3 ${kpis >= 5 ? "lg:grid-cols-5" : "lg:grid-cols-4"}`}>
          {Array.from({ length: kpis }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] w-full rounded-xl" />
          ))}
        </div>
      )}
      {lateral ? (
        <div className="grid items-start gap-5 lg:grid-cols-[300px_minmax(0,1fr)]">
          <Skeleton className="h-96 rounded-lg" />
          <Skeleton className="h-96 rounded-lg" />
        </div>
      ) : (
        <>
          <Skeleton className="h-64 w-full rounded-lg" />
          <TablaSkeleton filas={4} columnas={6} />
        </>
      )}
    </div>
  );
}
